<template>
    <div>
        <div :class="searchAutocompleteClass">
            <div v-if="upperLabel" class="form-input-text--label">
                <span class="form-input-text--label__text">{{ upperLabel }}:</span>
            </div>
            <input
                v-if="!selectedClient"
                v-model="searchTerm"
                type="text"
                class="search-autocomplete__input"
                :placeholder="$t('pages.company.company_clients.search_clients')"
                @keyup="inputChange"
            />

            <div v-else>
                <div>
                    <label v-if="!upperLabel" class="form-field__label">
                        {{ $t('pages.company.company_clients.choosen_client') }}
                    </label>
                    <div :class="formFieldLook">
                        <common-user-avatar :user="selectedClient" :size="avatarSize"></common-user-avatar>
                        <div>
                            <p>{{ selectedClient.firstName }} {{ selectedClient.lastName }}</p>
                            <p>{{ selectedClient.mobile }}</p>
                        </div>
                        <common-icon name="trash" size="medium" @click="removeSelectedClient()"></common-icon>
                    </div>
                </div>
            </div>

            <div v-if="!!items.length" class="search-autocomplete__suggestions">
                <div
                    v-for="(item, index) in items"
                    :key="index"
                    class="search-autocomplete__suggestions--item"
                    @click="selectClient(item)"
                >
                    <common-user-avatar :user="item" :size="avatarSize"></common-user-avatar>
                    <div>
                        <p>{{ item.firstName }} {{ item.lastName }}</p>
                        <p>{{ item.mobile }}</p>
                    </div>
                </div>
            </div>
            <div v-if="searchTerm.length > 2 && !items.length" class="search-autocomplete__suggestions">
                <div class="search-autocomplete__suggestions--item search-autocomplete__suggestions--item--no-results">
                    <span>{{ $t('common.microcopy.no_results') }}</span>
                    <common-button
                        v-if="addNewClientAvailable"
                        type="link"
                        :label="$t('pages.company.company_clients.add_client')"
                        @click="emitEvent({ name: 'toggle-client-preview' })"
                    ></common-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import debounce from 'lodash.debounce'
    import CompanyAPI from 'api/CompanyAPI'

    export default {
        props: {
            companyId: { type: Number, required: true },
            upperLabel: { type: String, required: false, default: '' },
            addNewClientAvailable: { type: Boolean, required: false, default: true },
            avatarSize: { type: String, required: false, default: 'medium' },
            inputStyle: { type: String, required: false, default: '' },
            chosenClient: { type: Object, required: false, default: undefined },
        },

        data() {
            return {
                searchTerm: '',
                items: [],
                selectedClient: null,
            }
        },

        computed: {
            searchAutocompleteClass() {
                return {
                    'search-autocomplete__search-input': true,
                    'form-input-text': true,
                }
            },

            avatarSizeClass() {
                return {
                    [this.avatarSize]: true,
                }
            },

            formFieldLook() {
                return {
                    [`search-autocomplete__selected-user--${this.inputStyle}`]: true,
                    'search-autocomplete__selected-user': true,
                }
            },
        },

        methods: {
            inputChange: debounce(function () {
                if (this.searchTerm.length > 2) {
                    const request = {
                        filter: this.searchTerm,
                    }
                    CompanyAPI.getCompanyClients(this.companyId, request).then((val) => {
                        if (val.page.length > 0) {
                            this.items = val.page.filter((item) => item.firstName && item.lastName && item.mobile)
                        } else {
                            this.items = []
                        }
                    })
                } else {
                    this.items = []
                }
            }, 500),

            selectClient(item) {
                this.searchTerm = ''
                this.items = []
                this.selectedClient = item
                this.emitEvent({ name: 'client-selected', payload: item.id })
            },

            removeSelectedClient() {
                this.selectedClient = null
                this.emitEvent({ name: 'client-removed' })
            },
        },

        mounted() {
            if (this.chosenClient && Object.keys(this.chosenClient).length) {
                this.selectedClient = this.chosenClient
            }
        },
    }
</script>

<style lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';

    .form-field__label {
        margin-bottom: 10px !important;
        display: block;
        color: #999999;
        font-size: 13px;
    }

    .search-autocomplete {
        &__search-input {
            position: relative;

            input {
                &::placeholder {
                    color: $subdomain-medium1;
                }

                &:disabled {
                    background-color: white;
                    opacity: 1;
                    pointer-events: none;
                    &::placeholder {
                        color: white;
                    }
                }
            }
        }

        &__selected-user {
            display: flex;
            align-items: center;

            .avatar {
                margin-right: 10px;
            }

            .icon {
                cursor: pointer;
                color: $color--accent-1;
                margin-left: 40px;
            }

            &--input {
                appearance: none;
                border: 1px solid $color--border-input;
                border-radius: 4px;
                background: $color--bg-input;
                padding-left: 15px;
            }
        }

        &__suggestions {
            @include breakpoint(smartphone) {
                max-width: 100%;
            }
            position: absolute;
            top: 60px;
            background: white;
            width: 100%;
            border: 1px solid $color--border-switch;
            border-radius: 6px;
            box-shadow: 2px 2px 8px 0 rgba(0, 0, 0, 0.2);
            z-index: 4;
            max-height: 300px;
            overflow-y: auto;
            padding: 10px 0;

            &--item {
                display: flex;
                align-items: center;
                padding: 10px;

                &--no-results {
                    justify-content: space-between;
                }

                &:hover {
                    background-color: $color-bg-menubar-1;
                    cursor: pointer;
                }

                .avatar {
                    margin-right: 10px;
                }
            }
        }

        &__single-item--selected {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding-right: 10px;
            background: $color-bg-menubar-1;
            cursor: auto;

            :hover {
                background: $color-bg-menubar-1;
            }
        }

        &__user-label {
            position: absolute;
            top: 6px;
            left: 50px;

            span {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                width: 160px;
            }
        }
    }

    .search-results {
        display: flex;
        padding-left: 10px;
        align-items: center;
        height: 50px;
    }

    .avatar-container {
        margin: 0 6px;

        img {
            border-radius: 50%;
            width: 40px;
            margin: 5px 0px;
        }

        p {
            margin: auto;
            font-size: 14px;
            line-height: 21px;
            font-weight: 500;
            color: white;
        }
    }

    .user-info-container {
        margin-left: 12px;

        p {
            color: #999999;
            font-size: 12px;
            line-height: 18px;

            span:first-of-type {
                font-family: 'Poppins';
                font-size: 14px;
                line-height: 21px;
                color: $color--txt-header-1;
            }
        }
    }

    .user-info-description {
        font-size: 12px !important;
        line-height: 18px !important;
        color: $color--txt-base-3 !important;
    }
</style>
